import { api } from './api';
import { studentApi } from './studentApi';
import { adminApi } from './adminApi';

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  timestamp?: string;
  errorCode?: string;
}

export interface AbacusBootstrapValidationReport {
  missingContent?: string[];
  missingQuizConfig?: string[];
  unknownSkillTags?: string[];
  orphanNodes?: string[];
  missingPools?: string[];
}

export interface AbacusBootstrapResult {
  courseId?: number;
  courseCreated?: boolean;
  syllabusVersion?: string;
  topicsCreated?: number;
  metadataCreated?: number;
  poolsGenerated?: number;
  structureNodes?: number;
  message?: string;
  validationReport?: AbacusBootstrapValidationReport;
}
export type AbacusTrack = 'JUNIOR' | 'SENIOR';
export type AbacusAccessTier = 'FREE_SELF_STUDY' | 'PAID_TUTOR_AIDED';

export interface AbacusDashboardProgressDTO {
  courseId: number;
  enrollmentId: number;
  track: string;
  levelCode: string;
  levelTitle: string;
  topicsCompleted: number;
  totalTopics: number;
  nextTestWeekTitle?: string | null;
  nextTestWeekPending: boolean;
  levelBadges?: string[];
}

export interface AbacusPlacementResultDTO {
  recommendedTrack: 'JUNIOR' | 'SENIOR' | string;
  message: string;
  applied: boolean;
  seniorCourseAvailable: boolean;
  courseId?: number | null;
}

export interface AbacusCourseStructureDTO {
  enrollmentId: number;
  courseId: number;
  courseName: string;
  track: AbacusTrack | null;
  currentLevelCode: string | null;
  levels: AbacusLevelDTO[];
}

export interface AbacusLevelDTO {
  levelCode: string;
  title: string;
  unlocked: boolean;
  completed: boolean;
  topicsCompleted: number;
  totalTopics: number;
  categories: AbacusCategoryDTO[];
}

export interface AbacusCategoryDTO {
  key: 'ABACUS_FINGER_THEORY' | 'ABACUS' | 'MENTAL_THEORY' | string;
  title: string;
  topics: AbacusTopicCardDTO[];
}

export interface AbacusTopicCardDTO {
  topicId: number;
  title: string;
  contentMode: 'THEORY' | 'DRILL' | string;
  viewed: boolean;
  practiceAttempts: number;
  lastScorePercent?: number | null;
  isTestWeek?: boolean;
  assessmentKind?: 'PRACTICE' | 'TEST_WEEK' | string;
  minPassPercent?: number | null;
  maxAttempts?: number | null;
  attemptsUsed?: number | null;
  lastPassed?: boolean | null;
  locked?: boolean;
  lockReason?: string | null;
  skillTags?: string[];
}

export interface AbacusGateResourceStatus {
  resourceId: number;
  type: string;
  title?: string;
  url?: string;
  consumed: boolean;
  consumptionHint?: string;
}

export interface AbacusTopicDetailsDTO {
  topicId: number;
  title: string;
  levelCode: string;
  category: string;
  contentMode: 'THEORY' | 'DRILL' | string;
  contentViewed: boolean;
  theoryContent?: string | null;
  gateStatus?: {
    topicFullyViewed: boolean;
    resources: AbacusGateResourceStatus[];
  };
  testWeekGatePreview?: {
    prerequisiteTopics: Array<{ topicId: number; title: string; fullyViewed: boolean }>;
    canStart: boolean;
    missingTopicIds: number[];
  };
  resources?: Array<{
    id: number;
    type: 'VIDEO' | 'PDF' | 'IMAGE' | 'AUDIO' | 'INTERACTIVE' | string;
    url: string;
    title?: string;
  }>;
  practiceQuiz?: {
    quizId: number;
    title: string;
    questions: Array<{
      id?: string;
      questionId?: string;
      type?: string;
      questionType?: string;
      content?: string;
      prompt?: string;
      promptSpoken?: string;
      correctAnswer?: number;
      answer?: number;
      points?: number;
      tolerance?: number;
      options?: Record<string, unknown>;
    }>;
  } | null;
  interactive?: {
    type: 'VIRTUAL_ABACUS';
    columns?: number;
    targetNumber?: number;
  } | null;
}

export interface AbacusRegistrationTileCourse {
  id: number;
  name: string;
  registrationMethod?: string;
}

export interface AbacusRegistrationTileRef {
  id: number;
  name: string;
}

export interface AbacusRegistrationTile {
  variant: AbacusTrack;
  label: string;
  courses: AbacusRegistrationTileCourse[];
  comingSoonTeaser?: AbacusRegistrationTileRef | null;
  enrolledCourse?: AbacusRegistrationTileRef | null;
}

export interface AbacusAvailableTilesResponse {
  tiles: AbacusRegistrationTile[];
}

export interface ParentAbacusTestWeek {
  title: string;
  passed: boolean;
  scorePercent: number;
  completedAt?: string;
}

export interface ParentAbacusSummaryDTO {
  childId: number;
  track: AbacusTrack;
  levelCode: string;
  topicsCompleted: number;
  latestTestWeek?: ParentAbacusTestWeek | null;
  practiceCountWeek?: number;
  tutorAided?: boolean;
  accessTier?: AbacusAccessTier;
  levelBadges?: string[];
}

export interface ParentAbacusDetailDTO {
  childId: number;
  enrollmentId?: number;
  courseId?: number;
  track: AbacusTrack;
  levelCode: string;
  tutorAided: boolean;
  practiceCountWeek: number;
  testWeeks: ParentAbacusTestWeek[];
}

export interface TimedDrillView {
  attemptId: number;
  endsAt?: string;
  durationSec: number;
  levelNumber?: number;
  skillTags?: string[];
  questionId?: string | null;
  prompt?: string | null;
  correct?: boolean | null;
  correctCount: number;
  finished: boolean;
  unlocksLevel: boolean;
}

export interface LeaderboardView {
  rows: Array<{ displayName: string; score: number; rank: number }>;
  myRank?: number | null;
}

export interface CompetitionView {
  id: number;
  title: string;
  levelBand: string;
  skillTags?: string;
  startAt: string;
  endAt: string;
  poolMode: string;
  joined?: boolean;
}

export interface TutorAbacusSessionDTO {
  id: number;
  enrollmentId?: number;
  courseId?: number;
  tutorId: number;
  learnerId: number;
  learnerName: string;
  levelCode?: string;
  lockedLevelCount?: number;
  track?: AbacusTrack;
  scheduledAt: string;
  status: 'SCHEDULED' | 'COMPLETED' | 'CANCELLED' | 'MISSED' | string;
  joinUrl?: string;
  videoCallUrl?: string;
  plannedTopicIds?: number[];
  topics?: Array<{ topicId: number; resourceIds: number[] }>;
  actualDurationMinutes?: number | null;
  topicsCovered?: number[];
  recordingStored?: boolean;
  topicsPlanned?: string[];
}

export interface TutorSessionOffer {
  enrollmentId: number;
  courseId: number;
  learnerId: number;
  accessTier: AbacusAccessTier;
  bookable: boolean;
  upsell: boolean;
  assignedTutorId?: number | null;
  levelCode?: string | null;
  lockedLevelCount: number;
  suggestedTopicId?: number | null;
}

export interface CreateTutorAbacusSessionRequest {
  scheduledAt: string;
  plannedTopicIds: number[];
}

export interface VoiceAttemptAnswerPayload {
  questionId: string;
  promptSpoken: string;
  expectedAnswer: number;
  answerRaw: string;
  inputMethod: 'VOICE' | 'TYPED';
  sttConfidence?: number | null;
  tolerance?: number;
}

export interface AbacusAssessmentView {
  assessmentKind: 'PRACTICE' | 'TEST_WEEK' | string;
  poolMode?: string;
  formative?: boolean;
  minPassPercent?: number;
  maxQuestions?: number;
  maxAttempts?: number | null;
  attemptsUsed?: number;
  attemptsRemaining?: number | null;
  thresholdSource?: 'TOPIC' | 'COURSE' | 'SYSTEM' | string;
  interQuestionPauseMs?: number;
  lastPassed?: boolean | null;
}

export interface StartedAbacusQuiz extends AbacusAssessmentView {
  softGateRequired?: boolean;
  message?: string;
  questions?: NonNullable<AbacusTopicDetailsDTO['practiceQuiz']>['questions'];
}

export interface VoiceAttemptResult {
  attemptId: number;
  topicId: number;
  enrollmentId: number;
  quizType: string;
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  assessmentKind?: string;
  passed?: boolean;
  formative?: boolean;
  minPassPercent?: number;
  maxAttempts?: number | null;
  attemptsUsed?: number;
  attemptsRemaining?: number | null;
  answers: Array<{
    questionId: string;
    promptSpoken: string;
    expectedAnswer: number;
    answerRaw: string;
    answerNormalized: number | null;
    inputMethod: 'VOICE' | 'TYPED';
    sttConfidence?: number | null;
    correct: boolean;
  }>;
}

export interface AbacusOfflineBundleDTO {
  levelCode: string;
  fileName: string;
  downloadUrl: string;
  sizeBytes?: number;
}

const unwrap = <T>(response: { data: ApiResponse<T> | T }): T => {
  const payload = response.data as ApiResponse<T>;
  if (typeof payload === 'object' && payload !== null && 'success' in payload && 'data' in payload) {
    return payload.data;
  }
  return response.data as T;
};

export const abacusService = {
  // Student APIs
  async getStudentCourseStructure(courseId: number): Promise<AbacusCourseStructureDTO> {
    const response = await studentApi.get<ApiResponse<AbacusCourseStructureDTO>>(
      `/api/student/abacus/courses/${courseId}/structure`
    );
    return unwrap(response);
  },

  async getStudentTopic(courseId: number, topicId: number): Promise<AbacusTopicDetailsDTO> {
    const response = await studentApi.get<ApiResponse<AbacusTopicDetailsDTO>>(
      `/api/student/abacus/courses/${courseId}/topics/${topicId}`
    );
    return unwrap(response);
  },

  async recordContentView(
    courseId: number,
    topicId: number,
    body: {
      enrollmentId: number;
      resourceId: number;
      consumptionMethod: string;
      dwellSeconds: number;
    }
  ): Promise<{ resourceConsumed: boolean; topicFullyViewed: boolean }> {
    const response = await studentApi.post<
      ApiResponse<{ resourceConsumed: boolean; topicFullyViewed: boolean }>
    >(`/api/student/abacus/courses/${courseId}/content-view/${topicId}`, body);
    return unwrap(response);
  },

  async getAssessmentConfig(topicId: number, enrollmentId: number): Promise<AbacusAssessmentView> {
    const response = await studentApi.get<ApiResponse<AbacusAssessmentView>>(
      `/api/student/abacus/topics/${topicId}/assessment-config`,
      { params: { enrollmentId } }
    );
    return unwrap(response);
  },

  async startPracticeQuiz(
    topicId: number,
    enrollmentId: number,
    overrideSoftGate = false
  ): Promise<StartedAbacusQuiz> {
    const response = await studentApi.post<ApiResponse<StartedAbacusQuiz>>(
      `/api/student/abacus/practice/${topicId}`,
      null,
      { params: { enrollmentId, overrideSoftGate } }
    );
    return unwrap(response);
  },

  async submitVoiceAttempt(
    topicId: number,
    enrollmentId: number,
    body: { answers: VoiceAttemptAnswerPayload[] }
  ): Promise<VoiceAttemptResult> {
    const response = await studentApi.post<ApiResponse<VoiceAttemptResult>>(
      `/api/student/abacus/practice/${topicId}/voice-attempt`,
      body,
      { params: { enrollmentId } }
    );
    return unwrap(response);
  },

  async listVoiceAttempts(topicId: number, enrollmentId: number): Promise<VoiceAttemptResult[]> {
    const response = await studentApi.get<ApiResponse<VoiceAttemptResult[]>>(
      `/api/student/abacus/practice/${topicId}/voice-attempts`,
      { params: { enrollmentId } }
    );
    return unwrap(response);
  },

  async startTestWeek(topicId: number, enrollmentId: number): Promise<StartedAbacusQuiz> {
    const response = await studentApi.post<ApiResponse<StartedAbacusQuiz>>(
      `/api/student/abacus/test-week/${topicId}`,
      null,
      { params: { enrollmentId } }
    );
    return unwrap(response);
  },

  async getDashboardProgress(): Promise<AbacusDashboardProgressDTO> {
    const response = await studentApi.get<ApiResponse<AbacusDashboardProgressDTO>>(
      '/api/student/abacus/dashboard'
    );
    return unwrap(response);
  },

  async recommendPlacement(answers: number[]): Promise<AbacusPlacementResultDTO> {
    const response = await studentApi.post<ApiResponse<AbacusPlacementResultDTO>>(
      '/api/student/abacus/placement',
      { answers }
    );
    return unwrap(response);
  },

  async confirmPlacement(recommendedTrack: string): Promise<AbacusPlacementResultDTO> {
    const response = await studentApi.post<ApiResponse<AbacusPlacementResultDTO>>(
      '/api/student/abacus/placement/confirm',
      { recommendedTrack }
    );
    return unwrap(response);
  },

  async fetchAvailableAbacusTiles(): Promise<AbacusAvailableTilesResponse> {
    const response = await studentApi.get<ApiResponse<AbacusAvailableTilesResponse>>(
      '/api/student/courses/available-abacus'
    );
    return unwrap(response);
  },

  async getOfflineBundle(courseId: number, levelCode: string): Promise<AbacusOfflineBundleDTO> {
    const response = await studentApi.post<ApiResponse<AbacusOfflineBundleDTO>>(
      `/api/student/abacus/courses/${courseId}/offline-bundles`,
      { levelCode }
    );
    return unwrap(response);
  },

  async fetchLevelBundle(courseId: number, levelCode: string) {
    const response = await studentApi.get<ApiResponse<import('../pwa/offlineBundle').LevelBundle>>(
      `/api/student/abacus/courses/${courseId}/levels/${encodeURIComponent(levelCode)}/offline-bundle`
    );
    return unwrap(response);
  },

  async syncPractice(body: {
    attempts: Array<{
      clientAttemptId: string;
      topicId: number;
      enrollmentId: number;
      answers: Array<{
        questionId: string;
        promptSpoken: string;
        expectedAnswer: number;
        answerRaw: string;
        inputMethod: 'VOICE' | 'TYPED';
        sttConfidence?: number | null;
        tolerance?: number;
      }>;
    }>;
  }) {
    const response = await studentApi.post<ApiResponse<{
      results: Array<{
        clientAttemptId: string;
        attemptId: number;
        score: number;
        duplicate: boolean;
        unlocksLevel: boolean;
      }>;
    }>>('/api/student/abacus/practice/sync', body);
    return unwrap(response);
  },

  // Parent APIs
  async getParentAbacusSummaries(): Promise<ParentAbacusSummaryDTO[]> {
    const response = await api.get<ApiResponse<ParentAbacusSummaryDTO[]>>('/api/parent/abacus/children/summary');
    return unwrap(response);
  },

  async getParentAbacusDetail(childId: number): Promise<ParentAbacusDetailDTO> {
    const response = await api.get<ApiResponse<ParentAbacusDetailDTO>>(
      `/api/parent/abacus/child/${childId}/detail`
    );
    return unwrap(response);
  },

  // Tutor APIs
  async getTutorSessions(): Promise<TutorAbacusSessionDTO[]> {
    const response = await api.get<ApiResponse<TutorAbacusSessionDTO[]>>('/api/tutor/abacus/sessions');
    return unwrap(response);
  },

  async getTutorSessionOffer(enrollmentId: number): Promise<TutorSessionOffer> {
    const response = await api.get<ApiResponse<TutorSessionOffer>>(
      `/api/abacus/tutor-delivery/enrollments/${enrollmentId}/offer`
    );
    return unwrap(response);
  },

  async bookTutorSession(
    enrollmentId: number,
    body: CreateTutorAbacusSessionRequest
  ): Promise<TutorAbacusSessionDTO> {
    const response = await api.post<ApiResponse<TutorAbacusSessionDTO>>(
      `/api/abacus/tutor-delivery/enrollments/${enrollmentId}/sessions`,
      body
    );
    return unwrap(response);
  },

  async getTutorDeliverySession(sessionId: number): Promise<TutorAbacusSessionDTO> {
    const response = await api.get<ApiResponse<TutorAbacusSessionDTO>>(
      `/api/abacus/tutor-delivery/sessions/${sessionId}`
    );
    return unwrap(response);
  },

  async endTutorSession(
    sessionId: number,
    body: { durationMinutes: number; topicsCovered: number[] }
  ): Promise<TutorAbacusSessionDTO> {
    const response = await api.post<ApiResponse<TutorAbacusSessionDTO>>(
      `/api/abacus/tutor-delivery/sessions/${sessionId}/end`,
      body
    );
    return unwrap(response);
  },

  async markTutorSessionMissed(sessionId: number): Promise<TutorAbacusSessionDTO> {
    const response = await api.post<ApiResponse<TutorAbacusSessionDTO>>(
      `/api/abacus/tutor-delivery/sessions/${sessionId}/missed`
    );
    return unwrap(response);
  },

  async getTutorTopic(sessionId: number, topicId: number): Promise<AbacusTopicDetailsDTO> {
    const response = await api.get<ApiResponse<AbacusTopicDetailsDTO>>(
      `/api/tutor/abacus/sessions/${sessionId}/topics/${topicId}`
    );
    return unwrap(response);
  },

  async setTutorEntitlement(
    enrollmentId: number,
    body: { accessTier: AbacusAccessTier; tutorId?: number }
  ): Promise<{ enrollmentId: number; accessTier: AbacusAccessTier; assignedTutorId?: number | null; cancelledSessionCount: number }> {
    const response = await adminApi.put<
      ApiResponse<{ enrollmentId: number; accessTier: AbacusAccessTier; assignedTutorId?: number | null; cancelledSessionCount: number }>
    >(`/api/admin/abacus/enrollments/${enrollmentId}/entitlement`, body);
    return unwrap(response);
  },

  // Admin APIs
  async runAdminBootstrap(body: {
    courseId?: number;
    track?: 'JUNIOR' | 'SENIOR';
    syllabusResource?: string;
  }): Promise<AbacusBootstrapResult> {
    const response = await adminApi.post<ApiResponse<AbacusBootstrapResult>>(
      '/api/admin/abacus/bootstrap',
      body,
      { timeout: 120000 }
    );
    return unwrap(response);
  },

  async startTimedDrill(): Promise<TimedDrillView> {
    const response = await studentApi.post<ApiResponse<TimedDrillView>>('/api/student/abacus/timed-drill/start');
    return unwrap(response);
  },

  async answerTimedDrill(attemptId: number, questionId: string, answer: number): Promise<TimedDrillView> {
    const response = await studentApi.post<ApiResponse<TimedDrillView>>(
      `/api/student/abacus/timed-drill/${attemptId}/answer`,
      { questionId, answer }
    );
    return unwrap(response);
  },

  async latestTimedDrill(): Promise<TimedDrillView | null> {
    try {
      const response = await studentApi.get<ApiResponse<TimedDrillView>>('/api/student/abacus/timed-drill/latest');
      return unwrap(response);
    } catch {
      return null;
    }
  },

  async setLeaderboardOptIn(optedIn: boolean): Promise<{ optedIn: boolean; displayName: string }> {
    const response = await studentApi.post<ApiResponse<{ optedIn: boolean; displayName: string }>>(
      '/api/student/abacus/leaderboard/opt-in',
      { optedIn }
    );
    return unwrap(response);
  },

  async getLeaderboard(): Promise<LeaderboardView> {
    const response = await studentApi.get<ApiResponse<LeaderboardView>>('/api/student/abacus/leaderboard');
    return unwrap(response);
  },

  async openCompetitions(): Promise<CompetitionView[]> {
    const response = await studentApi.get<ApiResponse<CompetitionView[]>>('/api/student/abacus/competitions/open');
    return unwrap(response);
  },

  async joinCompetition(eventId: number): Promise<CompetitionView> {
    const response = await studentApi.post<ApiResponse<CompetitionView>>(
      `/api/student/abacus/competitions/${eventId}/join`
    );
    return unwrap(response);
  },

  async scheduleCompetition(body: {
    title: string;
    levelBand: string;
    skillTags: string;
    startAt: string;
    endAt: string;
    poolMode: string;
  }): Promise<CompetitionView> {
    const response = await adminApi.post<ApiResponse<CompetitionView>>('/api/admin/abacus/competitions', body);
    return unwrap(response);
  },
};

export default abacusService;
